import { useCallback, useState } from 'react';
import { DocumentNode } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useAuth } from 'shared/providers/AuthProvider';
import { HttpMethods } from 'shared/enums/HttpMethods';
import http, { coreApiUrl } from 'shared/utils/http';

export function useFetchGraphQLQuery<T, TV>({
  query,
  variables,
  onSuccess,
}: {
  query: DocumentNode;
  variables?: TV;
  onSuccess?: (data: T) => void;
}): [
  (variableDynamic: TV) => Promise<{ data: T | undefined; error?: Error | null }>,
  { data: T | null; error: Error | null; loading: boolean },
] {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const { getToken } = useAuth();
  const { clinicUser } = useClinicUser();
  const fetchData = useCallback(
    async (variableDynamic?: Partial<TV>) => {
      try {
        if (!query) {
          throw new Error('Query is required');
        }
        const token = await getToken();
        if (!token) {
          throw new Error('No authentication token available');
        }
        const response = await http<{ data: T }>({
          method: HttpMethods.Post,
          url: coreApiUrl,
          token,
          clinicUser,
          body: JSON.stringify({
            operationName: getOperationName(query),
            query: query.loc?.source.body,
            variables: { ...variables, ...variableDynamic },
          }),
        });
        setData(response.data);
        onSuccess?.(response.data);
        return response;
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch messages'));
        return { data: undefined, error: err instanceof Error ? err : new Error('Failed to fetch messages') };
      } finally {
        setLoading(false);
      }
    },
    [clinicUser, getToken, query, variables, onSuccess],
  );
  return [fetchData, { data, error, loading }];
}
